import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { socialAccounts } from './SocialIcons';

// Icon Components (Inline SVG)
const MenuIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const ExternalIcon = () => (
  <svg className="inline-block w-3 h-3 ml-1 opacity-60" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const Navbar = () => {
  const [isSolid, setIsSolid] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Desktop dropdowns: hovering previews a menu, clicking (or Enter/Space)
  // pins it open so it works on touch and keyboard too.
  const [openMenu, setOpenMenu] = useState(null);
  const [pinned, setPinned] = useState(false);
  const [openNested, setOpenNested] = useState(null);
  const desktopNavRef = useRef(null);
  const location = useLocation();

  const closeMenus = useCallback(() => {
    setOpenMenu(null);
    setPinned(false);
    setOpenNested(null);
  }, []);

  const toggleMenu = (name) => {
    if (openMenu === name && pinned) {
      closeMenus();
    } else {
      setOpenMenu(name);
      setPinned(true);
      setOpenNested(null);
    }
  };

  const handlePointerEnter = (name, e) => {
    if (e.pointerType !== 'mouse' || (openMenu === name && pinned)) return;
    setOpenMenu(name);
    setPinned(false);
    setOpenNested(null);
  };

  const handlePointerLeave = (e) => {
    if (e.pointerType === 'mouse' && !pinned) closeMenus();
  };

  // Tabbing out of an open menu closes it (relatedTarget is null for plain clicks)
  const handleBlur = (e) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) closeMenus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && openMenu) {
      const trigger = document.getElementById(`nav-menu-${slug(openMenu)}-button`);
      closeMenus();
      if (trigger) trigger.focus();
    }
  };

  // Clicking anywhere outside the desktop menu closes it
  useEffect(() => {
    if (!openMenu) return undefined;
    const onPointerDown = (e) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(e.target)) closeMenus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [openMenu, closeMenus]);

  useEffect(() => {
    const handleScroll = () => {
      setIsSolid(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    {
      name: 'About',
      href: '/about',
      submenu: [
        { name: 'Administrative Team', href: '/administrative-team' },
        { name: 'Mission Statement', href: '/mission-statement' },
        { name: "Principal's Message", href: '/principals-message' },
        { name: 'Leadership Directory', href: '/staff-directory' },
        {
          name: 'NYCPS District Links',
          href: '#',
          submenu: [
            {
              name: 'NYC Public Schools',
              href: 'https://www.schools.nyc.gov/',
              external: true
            },
            {
              name: 'PSMS72 Lexington Academy Information',
              href: 'https://www.schools.nyc.gov/schools/M072',
              external: true
            }
          ]
        },
        {
          name: 'School Quality Snapshot',
          href: 'https://tools.nycenet.edu/snapshot/2022/04M072/EMS/?utm_source=schools.nyc.gov&utm_medium=Public_Page&utm_campaign=School_Page',
          external: true
        },
        {
          name: 'School Performance Dashboard',
          href: 'https://tools.nycenet.edu/dashboard/?utm_source=schools.nyc.gov&utm_medium=Public_Page&utm_campaign=School_Page#dbn=04M072&report_type=EMS&view=City',
          external: true
        },
        {
          name: 'CBO',
          href: 'https://docs.google.com/presentation/d/1zc6219JC7xL-kSFSiaoRFpM3U4TpKWOf/edit?pli=1&slide=id.p1#slide=id.p1',
          external: true
        },
      ]
    },
    {
      name: 'Policies/Procedures',
      href: '/policies-procedures',
      submenu: [
        { name: 'Cell Phone Policy', href: '/cell-phone-policy' },
        { name: 'Parent/Student Handbook', href: '/handbook' },
        { name: 'Uniform Policy', href: '/uniform-policy' },
      ]
    },
    {
      name: 'Students',
      href: '/students',
      submenu: [
        { name: 'Mental Health Resources', href: '/mental-health-resources' },
        { name: 'Student Handbook', href: '/handbook' },
      ]
    },
    {
      name: 'Parents',
      href: '/parents',
      submenu: [
        {
          name: 'Get Involved',
          href: 'https://www.schools.nyc.gov/get-involved/families',
          external: true
        },
        { name: 'Mental Health Resources', href: '/mental-health-resources' },
      ]
    },
    {
      name: 'Academics',
      href: '/academics',
      submenu: [
        { name: 'Dual Language & ENL Program', href: '/dual-language-program' },
        { name: 'Learning Resources', href: '/learning-resources' },
        { name: 'Classes', href: '/classes' },
      ]
    },
    { name: 'Calendar', href: '/calendar' },
    { name: 'Gallery', href: '/gallery' },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-primary focus:rounded-lg focus:shadow-lg"
      >
        Skip to content
      </a>

      <div className="mx-auto max-w-[1400px] px-4">
        <nav
          className={`relative mt-4 rounded-2xl transition-all duration-200 ease-out bg-white ${
            isSolid ? 'shadow-lg' : 'shadow-md'
          }`}
          aria-label="Primary navigation"
        >

          <div className="flex items-center justify-between px-6 py-3">
            {/* Logo & Name */}
            <a
              href="/"
              className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 rounded-lg"
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-150 group-hover:scale-110 shadow-md">
                <img 
                  src="/assets/images/logo.png" 
                  alt="The Lexington Academy logo" 
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-primary text-lg tracking-tight leading-tight">
                  The Lexington Academy
                </span>
                <span className="text-xs text-gray-600 font-medium">Home of the Lions</span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <div
              ref={desktopNavRef}
              onKeyDown={handleKeyDown}
              className="hidden min-[1300px]:flex items-center gap-1"
            >
              {navLinks.map((link) => {
                const triggerClasses =
                  'flex items-center gap-1 px-2 2xl:px-3 py-2.5 text-sm font-semibold transition-colors duration-150 rounded-lg hover:text-orange-700 hover:bg-orange-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2';

                if (!link.submenu) {
                  return (
                    <a
                      key={link.name}
                      href={link.href}
                      className={`${triggerClasses} text-navy-700`}
                      {...(location.pathname === link.href && { 'aria-current': 'page' })}
                    >
                      {link.name}
                    </a>
                  );
                }

                const isOpen = openMenu === link.name;
                const menuId = `nav-menu-${slug(link.name)}`;
                const inSection = link.submenu.some((item) => item.href === location.pathname);

                return (
                  <div
                    key={link.name}
                    className="relative"
                    onPointerEnter={(e) => handlePointerEnter(link.name, e)}
                    onPointerLeave={handlePointerLeave}
                    onBlur={handleBlur}
                  >
                    <button
                      type="button"
                      id={`${menuId}-button`}
                      aria-expanded={isOpen}
                      aria-controls={menuId}
                      onClick={() => toggleMenu(link.name)}
                      className={`${triggerClasses} ${
                        isOpen || inSection ? 'text-orange-700' : 'text-navy-700'
                      } ${isOpen ? 'bg-orange-50' : ''}`}
                    >
                      {link.name}
                      <span className={`transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}>
                        <ChevronDownIcon />
                      </span>
                    </button>

                    {/* Dropdown Menu. The padding (not a margin) bridges the gap so
                        the pointer never leaves the menu on its way down. */}
                    <div
                      id={menuId}
                      className={`absolute top-full left-0 pt-2 w-64 transition-all duration-150 ease-out z-50 ${
                        isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                      }`}
                    >
                      <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2">
                        {link.submenu.map((item) => {
                          if (!item.submenu) {
                            return (
                              <a
                                key={item.name}
                                href={item.href}
                                className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-accent-light hover:text-primary transition-colors duration-150 border-l-2 border-transparent hover:border-secondary"
                                {...(item.external && {
                                  target: '_blank',
                                  rel: 'noopener noreferrer'
                                })}
                              >
                                <span>
                                  {item.name}
                                  {item.external && <ExternalIcon />}
                                </span>
                              </a>
                            );
                          }

                          const nestedKey = `${link.name}/${item.name}`;
                          const nestedOpen = openNested === nestedKey;
                          const nestedId = `nav-menu-${slug(nestedKey)}`;

                          return (
                            <div key={item.name} className="relative group/nested">
                              <button
                                type="button"
                                aria-expanded={nestedOpen}
                                aria-controls={nestedId}
                                onClick={() => setOpenNested(nestedOpen ? null : nestedKey)}
                                className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-accent-light hover:text-primary transition-colors duration-150 border-l-2 border-transparent hover:border-secondary"
                              >
                                <span>{item.name}</span>
                                <svg className="w-4 h-4 -rotate-90" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>

                              {/* Nested Dropdown */}
                              <div
                                id={nestedId}
                                className={`absolute left-full top-0 pl-1 w-64 transition-all duration-150 ease-out z-50 ${
                                  nestedOpen
                                    ? 'opacity-100 visible'
                                    : 'opacity-0 invisible group-hover/nested:opacity-100 group-hover/nested:visible'
                                }`}
                              >
                                <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2">
                                  {item.submenu.map((nestedItem) => (
                                    <a
                                      key={nestedItem.name}
                                      href={nestedItem.href}
                                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-accent-light hover:text-primary transition-colors duration-150 border-l-2 border-transparent hover:border-secondary"
                                      {...(nestedItem.external && {
                                        target: '_blank',
                                        rel: 'noopener noreferrer'
                                      })}
                                    >
                                      {nestedItem.name}
                                      {nestedItem.external && <ExternalIcon />}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA & Mobile Menu Button */}
            <div className="flex items-center gap-3">
              {socialAccounts.length > 0 && (
                <div className="hidden min-[1400px]:flex items-center gap-1">
                  {socialAccounts.map(({ key, label, url, Icon }) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${label} (opens in a new tab)`}
                      className="p-2 rounded-lg text-gray-600 hover:text-orange-700 hover:bg-orange-50 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              )}

              <span id="site-language" className="inline-flex items-center"></span>

              <a
                href="/contact"
                className="hidden sm:inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-orange-700 rounded-full hover:bg-orange-800 transition-all duration-150 shadow-md hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
              >
                Contact Us
              </a>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="min-[1300px]:hidden p-2 text-primary hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="min-[1300px]:hidden border-t border-gray-100 bg-gray-50 max-h-[calc(100vh-5rem)] overflow-y-auto">
              <div className="px-4 py-3 space-y-1">
                {navLinks.map((link) => (
                  <div key={link.name}>
                    <a
                      href={link.href}
                      className="block px-4 py-2.5 text-sm font-semibold text-primary hover:text-orange-700 hover:bg-white rounded-lg transition-colors duration-150"
                    >
                      {link.name}
                    </a>
                    {link.submenu && (
                      <div className="ml-4 mt-1 space-y-1">
                        {link.submenu.map((item) => (
                          <div key={item.name}>
                            <a
                              href={item.href}
                              className="block px-4 py-2 text-sm text-gray-700 hover:text-orange-700 hover:bg-white rounded-lg transition-colors duration-150"
                              {...(item.external && !item.submenu && {
                                target: '_blank',
                                rel: 'noopener noreferrer'
                              })}
                            >
                              {item.name}
                              {item.external && !item.submenu && (
                                <svg className="inline-block w-3 h-3 ml-1 opacity-60" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              )}
                            </a>
                            {/* Nested submenu for mobile */}
                            {item.submenu && (
                              <div className="ml-4 mt-1 space-y-1">
                                {item.submenu.map((nestedItem) => (
                                  <a
                                    key={nestedItem.name}
                                    href={nestedItem.href}
                                    className="block px-4 py-2 text-xs text-gray-600 hover:text-orange-700 hover:bg-white rounded-lg transition-colors duration-150 pl-6"
                                    {...(nestedItem.external && {
                                      target: '_blank',
                                      rel: 'noopener noreferrer'
                                    })}
                                  >
                                    {nestedItem.name}
                                    {nestedItem.external && (
                                      <svg className="inline-block w-3 h-3 ml-1 opacity-60" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                    )}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {socialAccounts.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-4 pt-3">
                    {socialAccounts.map(({ key, label, url, Icon }) => (
                      <a
                        key={key}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-primary bg-white border border-gray-200 rounded-lg hover:text-orange-700 transition-colors duration-150"
                      >
                        <Icon className="w-5 h-5" />
                        {label}
                        <span className="sr-only">(opens in a new tab)</span>
                      </a>
                    ))}
                  </div>
                )}
                <a
                  href="/contact"
                  className="block px-4 py-2.5 text-sm font-semibold text-white bg-orange-700 rounded-lg hover:bg-orange-800 transition-all duration-150 text-center mt-3 shadow-md"
                >
                  Contact Us
                </a>
              </div>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
